import axios from 'axios'
import { store } from '../store'
import { toast } from '../components/ui/use-toast'

const BASE_URL = process.env.REACT_APP_GS1_BASE_URL

export const gs1Instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'ngrok-skip-browser-warning': 'true'
  }
})

// Refresh instance
const refreshAuthInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'ngrok-skip-browser-warning': 'true'
  }
})

const nonRefreshEndpoints = [
  '/gs1/standard/auth',
  '/gs1/standard/forgot-password',
  '/gs1/standard/reset-password',
  '/gs1/standard/refresh',
  '/gs1/standard/user/logout'
]

// ===== REFRESH STATE =====
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// ===== REQUEST INTERCEPTOR =====
gs1Instance.interceptors.request.use(
  config => {
    const state = store.getState()
    const accessToken = state.auth.auth?.accessToken

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  error => Promise.reject(error)
)

// ===== RESPONSE INTERCEPTOR =====
gs1Instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !nonRefreshEndpoints.includes(originalRequest.url)
    ) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: token => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(gs1Instance(originalRequest))
            },
            reject
          })
        })
      }

      isRefreshing = true

      try {
        const state = store.getState()
        const refreshToken = state.auth.auth?.refreshToken
        const accessToken = state.auth.auth?.accessToken

        const response = await refreshAuthInstance.post(
          '/gs1/standard/user/refresh',
          { refreshToken },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )

        const newAccessToken = response.data?.data?.tokens?.accessToken
        const newRefreshToken = response.data?.data?.tokens?.refreshToken
        if (!newAccessToken) throw new Error('No access token returned')
        if (!newRefreshToken) throw new Error('No refresh token returned')

        // Update Redux
        store.dispatch({
          type: 'auth/setauthData',
          payload: {
            ...state.auth.auth,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken
          }
        })

        processQueue(null, newAccessToken, newRefreshToken)

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return gs1Instance(originalRequest)
      } catch (err) {
        processQueue(err, null)

        toast({
          variant: 'destructive',
          title: 'Session Expired',
          description: 'Please login again'
        })

        store.dispatch({ type: 'auth/resetAuth' })
        store.dispatch({ type: 'persist/PURGE' })
        localStorage.clear()

        setTimeout(() => {
          window.location.href = '/'
        }, 1300)

        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default gs1Instance
